import {Skeleton, Table, nodeColumn} from '@sollapay/ui/components';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {TrustAccountStatusGroupHeaderSkeleton} from './TrustAccountStatusGroupHeader';
import {
  TRUST_ACCOUNT_COLUMN_WIDTHS,
  TRUST_ACCOUNT_TABLE_FRAME_CLASS,
  TRUST_ACCOUNT_TABLE_MIN_WIDTH
} from '../config';

import type {ReactNode} from 'react';

type ColumnId = keyof typeof TRUST_ACCOUNT_COLUMN_WIDTHS;

interface SkeletonRow {
  group: string;
}

const ROWS: SkeletonRow[] = [
  {group: 'active'},
  {group: 'active'},
  {group: 'active'},
  {group: 'setup'},
  {group: 'setup'}
];

const CELLS: Record<ColumnId, {headerKey: string; cell: ReactNode}> = {
  trustName: {
    headerKey: 'columns.trustName',
    cell: (
      <div className="flex items-center gap-4">
        <Skeleton className="size-8.5 shrink-0 rounded-md" />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
    )
  },
  balance: {headerKey: 'columns.balance', cell: <Skeleton className="h-5 w-24" />},
  type: {headerKey: 'columns.type', cell: <TwoLines />},
  team: {
    headerKey: 'columns.team',
    cell: (
      <div className="flex items-center gap-2">
        <Skeleton className="size-6 shrink-0 rounded-full" />
        <Skeleton className="h-3 w-6" />
      </div>
    )
  },
  developer: {headerKey: 'columns.seller', cell: <TwoLines />},
  createdAt: {headerKey: 'columns.date', cell: <Skeleton className="h-4 w-20" />}
};

/** Same frame, column widths and status-group dividers as the loaded list, so nothing shifts when data arrives. */
export function TrustAccountTableSkeleton() {
  const {t} = useTranslation('trustAccounts');
  const columns = useMemo(
    () =>
      (Object.keys(CELLS) as ColumnId[]).map(id =>
        nodeColumn<SkeletonRow>({
          id,
          header: t(CELLS[id].headerKey),
          enableSorting: false,
          width: TRUST_ACCOUNT_COLUMN_WIDTHS[id],
          cell: () => CELLS[id].cell
        })
      ),
    [t]
  );

  return (
    <div data-slot="trust-account-table-skeleton" className={TRUST_ACCOUNT_TABLE_FRAME_CLASS}>
      <Table
        variant="embedded"
        columns={columns}
        data={ROWS}
        minWidth={TRUST_ACCOUNT_TABLE_MIN_WIDTH}
        getGroupKey={getSkeletonGroupKey}
        renderGroupHeader={() => <TrustAccountStatusGroupHeaderSkeleton />}
      />
    </div>
  );
}

const getSkeletonGroupKey = (row: SkeletonRow) => row.group;

function TwoLines() {
  return (
    <div className="flex flex-col gap-1.5">
      <Skeleton className="h-4 w-20" />
      <Skeleton className="h-3 w-14" />
    </div>
  );
}
